import useLocalStorageState from "use-local-storage-state";

import { RecKind } from "@/generated/client/graphql";

export type DraftType = "draft" | "letter" | "call" | "card" | "task";
export type Draft = Draft.Email | Draft.Letter | Draft.Call | Draft.Card;
export namespace Draft {
  export interface Email {
    body: string;
    subject?: Exclude<string, "letter" | "card">;
  }
  export interface Letter {
    body: string;
    subject: "letter";
  }
  export interface Card {
    body: string;
    subject: "card";
  }
  export interface Call {
    points?: string;
    notes?: string;
  }
}

export function isDraftEmail(draft?: Draft): draft is Draft.Email {
  return !!draft && "subject" in draft && draft.subject !== "letter" && draft.subject !== "card";
}
export function isDraftLetter(draft?: Draft): draft is Draft.Letter {
  return !!draft && "subject" in draft && draft.subject === "letter";
}
export function isDraftCard(draft?: Draft): draft is Draft.Card {
  return !!draft && "subject" in draft && draft.subject === "card";
}
export function isDraftCall(draft?: Draft): draft is Draft.Call {
  return !!draft && ("points" in draft || "notes" in draft);
}
export function getDraftTypeFromDraft(draft?: Draft | undefined): DraftType | null {
  if (isDraftLetter(draft)) return "letter";
  if (isDraftCall(draft)) return "call";
  if (isDraftCard(draft)) return "card";
  if (isDraftEmail(draft)) return "draft";
  return null;
}

export function getDraftTypeFromKind(kind?: RecKind): DraftType | null {
  switch (kind) {
    case RecKind.Call:
      return "call";
    case RecKind.Card:
      return "card";
    case RecKind.Email:
      return "draft";
    case RecKind.Letter:
      return "letter";
    case RecKind.Task:
      return "task";
    case RecKind.LifeEvent:
    case RecKind.Meeting:
    case RecKind.Other:
    default:
      return null;
  }
}
export function getDraftType(draftOrKind?: Draft | RecKind | undefined): DraftType | null {
  if (typeof draftOrKind === "string") {
    return getDraftTypeFromKind(draftOrKind);
  } else {
    return getDraftTypeFromDraft(draftOrKind);
  }
}

function getLocalStorageDraftKey(recId: string, donorId: string) {
  return `DRAFT_${recId}_${donorId}`;
}

/**
 * Drafts are stored in browser, and uni-directionally synced to server. Read more at TC-953.
 *
 * @see {@link getLocalStorageDraft}, {@link editLocalStorageDraft} and {@link removeLocalStorageDraft}
 */
export function useLocalStorageDraft<T extends Draft>(recId: string, donorId: string) {
  return useLocalStorageState<T>(getLocalStorageDraftKey(recId, donorId));
}
